import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Lock, ShieldCheck, CreditCard, CheckCircle, Loader2, Key, Sparkles, Quote, Star, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePurchase } from '../context/PurchaseContext';
import { useAuth } from '../context/AuthContext';
import { TesterCodeModal } from '../components/course/TesterCodeModal';

export default function PurchasePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPurchased, hasAccess, testerMode, claimStripe } = usePurchase();
  const { isAuthenticated, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTesterModal, setShowTesterModal] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [claimMsg, setClaimMsg] = useState(null);
  const testerParam = new URLSearchParams(location.search).get('tester');
  const showTesterOption = testerParam === '1';

  // If already has access (purchased or tester), redirect to home
  if (hasPurchased || hasAccess) {
    return (
      <div className="max-w-2xl mx-auto text-center fade-in">
        <div className="bg-white rounded-3xl border-2 border-success/30 p-8 md:p-12">
          <div className="w-20 h-20 bg-success/10 rounded-full mx-auto mb-6 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-success" />
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-text mb-4">
            You Already Have Access!
          </h1>
          <p className="text-text-muted text-lg mb-8">
            {testerMode.isActive 
              ? "You have tester access to the Digital Safety Course."
              : "You've already purchased the Digital Safety Course."
            } Enjoy learning!
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-primary text-white hover:bg-primary-600 rounded-full px-8 py-6 text-lg font-bold"
          >
            Go to Course
          </Button>
        </div>
      </div>
    );
  }

  const handlePurchase = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Redirect to Stripe hosted Payment Link
      const paymentLink = 'https://buy.stripe.com/00w9ANemh4bAc1D2CTfjG03';
      window.location.href = paymentLink;
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className="max-w-4xl mx-auto fade-in">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
            <ShieldCheck className="w-4 h-4" />
            <span>Unlock Full Course Access</span>
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-text mb-4">
            Digital Safety Course
          </h1>
          <p className="text-text-muted text-lg max-w-2xl mx-auto">
            Get lifetime access to all 12 modules, interactive quizzes, checklists, and your completion certificate.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {/* What's included */}
          <div className="bg-white rounded-2xl border border-stone-200 p-6 md:p-8">
            <h2 className="font-heading font-bold text-xl text-text mb-6">
              What's Included
            </h2>
            <ul className="space-y-4">
              {[
                '12 comprehensive modules',
                'Interactive quizzes with feedback',
                'Practical checklists',
                'Password strength builder',
                'Emergency contact templates',
                'Completion certificate',
                'Lifetime access',
                'Progress saved automatically',
              ].map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-success flex-shrink-0 mt-0.5" />
                  <span className="text-text-muted">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Purchase card */}
          <div className="bg-gradient-to-br from-primary to-primary-700 rounded-2xl p-6 md:p-8 text-white">
            <div className="text-center mb-6">
              <p className="text-primary-100 text-sm uppercase tracking-wider mb-2">One-time payment</p>
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-heading font-extrabold">$19.99</span>
                <span className="text-primary-100">AUD</span>
              </div>
              <p className="text-primary-100 text-sm mt-2">Lifetime access • No subscription</p>
            </div>

            <Button
              onClick={handlePurchase}
              disabled={isLoading}
              data-testid="purchase-button"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-xl py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Purchase Now
                </>
              )}
            </Button>

            {error && (
              <div className="mt-4 bg-error/20 text-white rounded-lg p-3 text-sm text-center">
                {error}
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2 text-primary-100 text-sm">
              <Lock className="w-4 h-4" />
              <span>Secure payment via Stripe</span>
            </div>

            {/* Tester code link - only when ?tester=1 in URL */}
            {showTesterOption && (
              <div className="mt-4 pt-4 border-t border-white/20">
                <button
                  onClick={() => setShowTesterModal(true)}
                  data-testid="have-tester-code"
                  className="text-sm text-primary-200 hover:text-white transition-colors flex items-center justify-center gap-2 w-full"
                >
                  <Key className="w-4 h-4" />
                  Have a tester code?
                </button>
              </div>
            )}

            <div className="mt-4 text-center">
              <p className="text-primary-200 text-xs">
                By purchasing, you agree to our{' '}
                <Link to="/terms" data-testid="purchase-terms-link" className="underline hover:text-white">terms of service</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Testimonial */}
        <div
          data-testid="testimonial-card"
          className="mt-8 bg-white rounded-2xl border border-stone-200 p-6 md:p-8 shadow-sm"
        >
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 bg-primary/10 rounded-full items-center justify-center flex-shrink-0">
              <Quote className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex gap-0.5 mb-3" aria-label="5 star rating">
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-secondary text-secondary" />
                ))}
              </div>
              <p className="text-text text-base md:text-lg leading-relaxed mb-4 italic">
                "I was terrified of being scammed online. This course walked me through every step in plain English — I finally feel confident with my passwords and banking apps. Worth every cent."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center text-primary font-bold">
                  MJ
                </div>
                <div>
                  <p className="font-semibold text-text text-sm">Margaret J.</p>
                  <p className="text-text-muted text-xs">Barwon Heads, VIC</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Already purchased? Claim access */}
        <div data-testid="claim-section" className="mt-8 bg-white rounded-2xl border-2 border-stone-200 p-6 md:p-8">
          <div className="flex items-start gap-4 flex-col md:flex-row md:items-center">
            <div className="flex-1">
              <h3 className="font-heading font-bold text-lg text-text mb-1">Already purchased?</h3>
              <p className="text-text-muted">
                If you've paid before, {isAuthenticated ? "click below and we'll find your purchase and unlock the course." : "sign in with the same email you used at checkout and we'll unlock it automatically."}
              </p>
              {claimMsg && (
                <div className="mt-3 bg-stone-50 border border-stone-200 rounded-xl p-3 text-sm text-text" data-testid="claim-message">{claimMsg}</div>
              )}
            </div>
            <div className="flex gap-2 flex-shrink-0">
              {isAuthenticated ? (
                <Button
                  onClick={async () => {
                    setClaiming(true);
                    setClaimMsg(null);
                    try {
                      const data = await claimStripe();
                      if (data.granted) {
                        setClaimMsg('Access unlocked. Redirecting…');
                        setTimeout(() => navigate('/'), 1500);
                      } else {
                        setClaimMsg(data.message || 'No purchase found for your account email.');
                      }
                    } catch (err) {
                      setClaimMsg(err.message || 'Could not check your purchase.');
                    } finally {
                      setClaiming(false);
                    }
                  }}
                  disabled={claiming}
                  data-testid="claim-access-btn"
                  className="rounded-xl bg-primary text-white"
                >
                  {claiming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Claim access
                </Button>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="outline" className="rounded-xl" data-testid="purchase-signin">Sign in</Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="rounded-xl bg-primary text-white" data-testid="purchase-signup">Create account</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Module 1 Free note */}
        <div className="mt-8 text-center bg-secondary/10 rounded-2xl p-6 border border-secondary/30">
          <p className="text-text-muted">
            <strong className="text-text">Not sure yet?</strong> Module 1 (Introduction) is free! 
            Try it before you buy.
          </p>
          <Button
            variant="outline"
            onClick={() => navigate('/module/introduction')}
            className="mt-4 rounded-full px-6"
          >
            Try Module 1 Free
          </Button>
        </div>
      </div>

      {/* Tester Code Modal */}
      <TesterCodeModal 
        isOpen={showTesterModal} 
        onClose={() => setShowTesterModal(false)} 
      />
    </>
  );
}
